import { useState } from 'react';
import { Drawer, Box, Grid, Pagination } from 'components/ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@apollo/client/react';
import { FilterProvider, useFilter, initPlaceFilterValues } from 'contexts/filter';
import { DividerContainer } from 'components/styles_components/container';
import { useError } from 'contexts/error';
import { usePaginate } from 'contexts/paginate';
import FilterForm from 'components/place/filter_form';
import FilterTags from 'components/filter/tags';
import FlexibleInput from 'components/flexible_input';
import CreateButton from 'components/place/button/create';
import FilterButton from 'components/place/button/filter';
import PlacesQuery from 'queries/place/index';
import Index from 'components/place/index';
import { TitleTypography, SubtitleTypography } from 'components/styles_components/typography';

const IndexContainer = () => {
  const intl = useIntl();
  const { page, onPageChange } = usePaginate();
  const setError = useError();
  const { placesFilter, setPlacesFilter } = useFilter();
  const [filterVisible, setFilterVisible] = useState(false);
  const [search, setSearch] = useState(null);
  const [sortBy, setSortBy] = useState('name_asc');
  const {
    data = {
      space: { overPlacesLimit: false },
      places: { places: [], perPage: 10, totalPlaces: 0 },
    },
    loading,
  } = useQuery(PlacesQuery, {
    variables: {
      ...placesFilter,
      search: search,
      page: page,
      sortBy: sortBy,
    },
    cachePolicy: 'cache-and-network',
    onError: () => {
      setError('error.place.index');
    },
  });

  return (
    <FilterProvider>
      <TitleTypography variant="h1">
        <FormattedMessage id="navbar.places" />
      </TitleTypography>
      <SubtitleTypography variant="h2">
        <FormattedMessage id="place.total" values={{ count: data.places.placesTotal ?? 0 }} />
      </SubtitleTypography>
      {filterVisible && (
        <Drawer
          anchor="right"
          open={filterVisible}
          onClose={() => {
            setFilterVisible(false);
          }}
        >
          <FilterForm
            initialValues={placesFilter}
            defaultValues={initPlaceFilterValues}
            onSubmit={(values) => {
              onPageChange(1);
              setPlacesFilter(values);
            }}
            onClose={() => {
              setFilterVisible(false);
            }}
          />
        </Drawer>
      )}
      <Grid container spacing={1} alignItems="center" justifyContent="flex-end">
        <Grid size={{ xs: 12, sm: 'auto' }} sx={{ flexGrow: { sm: 1 }, minWidth: 0 }}>
          <FlexibleInput
            placeholder={intl.formatMessage({ id: 'flexible_filter.place' })}
            value={search}
            onChange={setSearch}
          />
        </Grid>
        <Grid size="auto" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <FilterButton
            onClick={() => setFilterVisible(!filterVisible)}
            initPlaceFilterValues={initPlaceFilterValues}
            placesFilter={placesFilter}
          />
          {data.space.adminRole && <CreateButton overPlacesLimit={data.space.overPlacesLimit} />}
        </Grid>
      </Grid>
      <FilterTags
        filterValues={placesFilter}
        defaultValues={initPlaceFilterValues}
        onFilterChange={setPlacesFilter}
      />
      <Index loading={loading} places={data.places} sortBy={sortBy} onSortChange={setSortBy} />
      <Box display="flex" justifyContent="center">
        {data.places.placesTotalPages > 1 && (
          <Pagination
            count={data.places.placesTotalPages}
            page={page}
            onChange={(_e, nextPage) => {
              onPageChange(nextPage);
            }}
          />
        )}
      </Box>
      <DividerContainer />
    </FilterProvider>
  );
};

export default IndexContainer;
